import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_CATEGORIES } from "@/content/legal";
import { useOperatorContacts } from "@/lib/operatorContacts";
import SEO from "@/components/SEO";

// Categories that have their own tracked route. The message can still be
// sent from here, but the dedicated route gives a reference and a status.
const DEDICATED_ROUTES = {
  booking_rental_complaint: { to: "/report?kind=complaint", label: "Report Centre complaint form",
    note: "Complaints sent there get a reference number you can track." },
  verification_correction: { to: "/report?kind=business", label: "Report Centre business form",
    note: "Business owners can use “I run this business” there to challenge what ConfirmPH shows." },
  privacy_request: { to: "/report?kind=privacy", label: "privacy request form",
    note: "Privacy requests sent there are tracked against their response deadline." },
  security_issue: { to: "/security", label: "security page",
    note: "Security issues have their own route and contact." },
};

const INITIAL = {
  category: "general_support",
  name: "",
  email: "",
  message: "",
  booking_reference: "",
  company_name: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const contacts = useOperatorContacts();
  const dedicated = DEDICATED_ROUTES[form.category];

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/contact", form);
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center"
           data-testid="contact-success">
        <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
        <h1 className="font-display text-2xl font-bold mt-4">Message received</h1>
        <p className="text-slate-600 mt-2">
          Thanks for reaching out. We'll get back to you at the email you provided.
          For urgent issues, you can also email us at <a className="underline" href={`mailto:${contacts.support}`}>{contacts.support}</a>.
        </p>
        <div className="mt-6 flex justify-center gap-3 text-sm">
          <button
            data-testid="contact-send-another"
            onClick={() => setSubmitted(false)}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50">
            Send another message
          </button>
          <Link to="/" className="px-4 py-2 rounded-lg btn-brand font-medium">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid="contact-page">
      <SEO
        title="Contact ConfirmPH"
        description="Get in touch with the ConfirmPH team about booking confirmations, rental partnerships, support questions, or business onboarding in the Philippines."
        canonical="/contact"
      />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Contact
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 inline-flex items-center gap-2">
        <Mail className="w-7 h-7 text-emerald-600" />
        Contact us
      </h1>
      <p className="text-slate-600 mt-2">
        Tell us what's going on. We'll get back to you at the email you provide.
      </p>
      <p className="text-xs text-slate-500 mt-2" data-testid="contact-parent-line">
        Support and billing:{" "}
        <a className="underline break-all" href={`mailto:${contacts.support}`}>{contacts.support}</a>
        {contacts.privacy !== contacts.support && (
          <>
            {" · "}Privacy:{" "}
            <a className="underline break-all" href={`mailto:${contacts.privacy}`}>{contacts.privacy}</a>
          </>
        )}
        {contacts.security !== contacts.support && (
          <>
            {" · "}Security:{" "}
            <a className="underline break-all" href={`mailto:${contacts.security}`}>{contacts.security}</a>
          </>
        )}
        . Complaints, safety concerns and privacy requests can also be sent through the{" "}
        <Link to="/report" className="underline">Report Centre</Link>, which gives you a reference
        to track.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4 bg-white rounded-xl border border-slate-200 p-6"
            data-testid="contact-form">
        <Field label="Category">
          <select value={form.category} onChange={update("category")}
                  data-testid="contact-category"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none">
            {CONTACT_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {dedicated && (
            <p className="mt-2 text-xs text-slate-600" data-testid="contact-route-hint">
              {dedicated.note} Use the{" "}
              <Link to={dedicated.to} className="underline">{dedicated.label}</Link>, or send your
              message here.
            </p>
          )}
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name">
            <input value={form.name} onChange={update("name")} required
                    data-testid="contact-name" maxLength={120}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={update("email")} required
                    data-testid="contact-email" maxLength={200}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Booking reference (optional)">
            <input value={form.booking_reference} onChange={update("booking_reference")}
                    data-testid="contact-booking-ref" maxLength={32}
                    placeholder="e.g. BK-A1B2C3D4"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none" />
          </Field>
          <Field label="Company name (optional)">
            <input value={form.company_name} onChange={update("company_name")}
                    data-testid="contact-company-name" maxLength={120}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none" />
          </Field>
        </div>
        <Field label="Message">
          <textarea value={form.message} onChange={update("message")} required
                     data-testid="contact-message" maxLength={4000} rows={6}
                     className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none" />
        </Field>

        <button type="submit" disabled={busy}
                 data-testid="contact-submit"
                 className="w-full px-4 py-2.5 rounded-lg btn-brand text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <Send className="w-4 h-4" />
          {busy ? "Sending…" : "Send message"}
        </button>
        <p className="text-[11px] text-slate-500 text-center" data-testid="contact-privacy-note">
          What you send here is used to answer your message and is processed as described in
          the <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
